import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  message,
} from 'antd';
import PropTypes from 'prop-types';
import React from 'react';

import {RQUserRegistrationRole} from '../../../../../ReactQuery/apis';

const GENDER_OPTIONS = [
  {value: 'male', label: 'Male'},
  {value: 'female', label: 'Female'},
  {value: 'other', label: 'Other'},
];

const STATUS_OPTIONS = ['ACTIVE', 'INACTIVE'];

const BLOOD_GROUP_OPTIONS = [
  'A+',
  'A-',
  'B+',
  'B-',
  'AB+',
  'AB-',
  'O+',
  'O-',
];

const MARITAL_STATUS_OPTIONS = [ 'MARRIED', 'UNMARRIED'];

export const RegisterPatient = ({open, onClose, onSuccess}) => {
  const [form] = Form.useForm();

  const registerPatient = RQUserRegistrationRole('PATIENT', {
    onSuccess: () => {
      message.success('Patient registered successfully');
      form.resetFields();
      onSuccess?.();
      onClose();
    },
    onError: err => {
      console.error('Registration Error:', err);
      console.error('Response:', err.response?.data);

      message.error(
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Registration failed',
      );
    },
  });

  const handleFinish = values => {
    console.log('Sending Payload:', {
      ...values,
      status: values.status || 'ACTIVE',
    });

    registerPatient.mutate({
      ...values,
      status: values.status || 'ACTIVE',
    });
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      destroyOnHidden
      width={680}
      title={(
        <span className={'text-primary-700 font-bold text-base'}>
          Register Patient
        </span>
      )}
    >
      <Form
        form={form}
        layout={'vertical'}
        onFinish={handleFinish}
        initialValues={{
          status: 'ACTIVE',
        }}
        className={'mt-4'}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={'Full Name'}
              name={'fullName'}
              rules={[
                {
                  required: true,
                  message: 'Please enter full name',
                },
                {
                  validator: (_, value) => {
                    if (!value) {return Promise.resolve();}

                    if (value.startsWith(' ')) {
                      return Promise.reject(
                        new Error('First character cannot be a space'),
                      );
                    }

                    if (!/^[A-Za-z ]+$/.test(value)) {
                      return Promise.reject(
                        new Error('Full name can contain only letters and spaces'),
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                placeholder={'e.g. John Doe'}
                onKeyPress={e => {
                  if (!/[A-Za-z ]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={'Email'}
              name={'email'}
              rules={[
                {
                  required:false,
                  message: 'Please enter email',
                },
                {
                  type: 'email',
                  message: 'Enter a valid email',
                },
              ]}
            >
              <Input placeholder={'e.g. john@example.com'} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={'Password'}
              name={'password'}
              rules={[
                {
                  required:false,
                  message: 'Please enter password',
                },
              ]}
            >
              <Input.Password placeholder={'Enter password'} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={'Mobile Number'}
              name={'mobileNumber'}
              rules={[
                {
                  required:false,
                  message: 'Please enter mobile number',
                },
                {
                  pattern: /^[0-9]{10}$/,
                  message: 'Enter a valid 10-digit mobile number',
                },
              ]}
            >
              <Input
                placeholder={'9876543210'}
                maxLength={10}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={'Age'}
              name={'age'}
              rules={[
                {
                  required:false,
                  message: 'Please enter age',
                },
                {
                  pattern: /^[0-9]+$/,
                  message: 'Age must contain only numbers',
                },
                {
                  validator: (_, value) => {
                    if (!value) {return Promise.resolve();}
                    const age = Number(value);
                    if (age < 1 || age > 120) {
                      return Promise.reject(new Error('Age must be between 1 and 120'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                placeholder={'32'}
                maxLength={3}
                onKeyPress={e => {
                  if (!/[0-9]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={'Gender'}
              name={'gender'}
              rules={[
                {
                  required:false,
                  message: 'Please select gender',
                },
              ]}
            >
              <Select
                placeholder={'Select Gender'}
                options={GENDER_OPTIONS}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={'Blood Group'}
              name={'bloodGroup'}
            >
              <Select
                placeholder={'Select Blood Group'}
                options={BLOOD_GROUP_OPTIONS.map(item => ({
                  value: item,
                  label: item,
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={'Occupation'}
              name={'occupation'}
            >
              <Input placeholder={'Engineer'} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={'Marital Status'}
              name={'maritalStatus'}
            >
              <Select
                placeholder={'Select Marital Status'}
                options={MARITAL_STATUS_OPTIONS.map(item => ({
                  value: item,
                  label: item,
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={'Status'}
              name={'status'}
            >
              <Select
                options={STATUS_OPTIONS.map(item => ({
                  value: item,
                  label: item,
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label={'Address'}
              name={'address'}
            >
              <Input.TextArea
                rows={3}
                placeholder={'Enter Address'}
              />
            </Form.Item>
          </Col>
        </Row>

        <div className={'flex justify-end gap-2 mt-3'}>
          <Button
            onClick={handleCancel}
            className={'btn-secondary'}
          >
            Cancel
          </Button>

          <Button
            type={'primary'}
            htmlType={'submit'}
            loading={registerPatient.isPending}
            className={'btn-primary'}
          >
            Register
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

RegisterPatient.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default RegisterPatient;
